/** @format */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
    ISupportTicket,
    ISupportTicketDetail,
    ISupportTicketMessage,
    ICreateSupportTicketPayload,
    IAdminSupportTicketRow,
    IAdminSupportTicketDetail,
    IAdminSupportListQuery,
    IAdminUpdateSupportTicketPayload,
} from "../../utility/types/collectTypes";
import {
    submitCreateSupportTicket,
    submitListMySupportTickets,
    submitGetMySupportTicket,
    submitAddSupportTicketMessage,
    submitReopenSupportTicket,
    submitListAdminSupportTickets,
    submitGetAdminSupportTicket,
    submitUpdateAdminSupportTicket,
    submitAddAdminSupportMessage,
    submitResolveAdminSupportTicket,
} from "../services/collectService";

// ─────────────────────────────────────────────────────────────────────────
// Collect Help & Support slice — end-user tickets + super-admin Incident
// Queue. Transient (blacklisted from persistence). `isLoading` covers list/
// detail reads; `isSubmitting` covers mutations so a create/reply spinner
// never clobbers the list spinner.
// ─────────────────────────────────────────────────────────────────────────

interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}

const EMPTY_PAGE: Pagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
};

interface CollectSupportState {
    // End-user
    myTickets: ISupportTicket[];
    myPagination: Pagination;
    currentTicket: ISupportTicketDetail | null;

    // Super admin
    adminTickets: IAdminSupportTicketRow[];
    adminPagination: Pagination;
    adminCurrentTicket: IAdminSupportTicketDetail | null;

    isLoading: boolean;
    isSubmitting: boolean;
    error: string | null;
    submitError: string | null;
}

const initialState: CollectSupportState = {
    myTickets: [],
    myPagination: EMPTY_PAGE,
    currentTicket: null,
    adminTickets: [],
    adminPagination: EMPTY_PAGE,
    adminCurrentTicket: null,
    isLoading: false,
    isSubmitting: false,
    error: null,
    submitError: null,
};

// ── End-user thunks ────────────────────────────────────────────────────────

export const fetchMySupportTickets = createAsyncThunk(
    "collectSupport/fetchMy",
    async (
        args: {
            businessId: string;
            status?: string;
            page?: number;
            limit?: number;
        },
        { rejectWithValue },
    ) => {
        try {
            return await submitListMySupportTickets(args.businessId, {
                status: args.status,
                page: args.page,
                limit: args.limit,
            });
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to load tickets.");
        }
    },
);

export const fetchMySupportTicket = createAsyncThunk(
    "collectSupport/fetchMyOne",
    async (
        args: { businessId: string; ticketId: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitGetMySupportTicket(
                args.businessId,
                args.ticketId,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to load ticket.");
        }
    },
);

export const createSupportTicket = createAsyncThunk(
    "collectSupport/create",
    async (
        args: { businessId: string; payload: ICreateSupportTicketPayload },
        { rejectWithValue },
    ) => {
        try {
            return await submitCreateSupportTicket(
                args.businessId,
                args.payload,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to submit ticket.");
        }
    },
);

export const addSupportTicketMessage = createAsyncThunk(
    "collectSupport/addMessage",
    async (
        args: { businessId: string; ticketId: string; body: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitAddSupportTicketMessage(
                args.businessId,
                args.ticketId,
                args.body,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to send reply.");
        }
    },
);

export const reopenSupportTicket = createAsyncThunk(
    "collectSupport/reopen",
    async (
        args: { businessId: string; ticketId: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitReopenSupportTicket(
                args.businessId,
                args.ticketId,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to reopen ticket.");
        }
    },
);

// ── Admin thunks ───────────────────────────────────────────────────────────

export const fetchAdminSupportTickets = createAsyncThunk(
    "collectSupport/fetchAdmin",
    async (query: IAdminSupportListQuery, { rejectWithValue }) => {
        try {
            return await submitListAdminSupportTickets(query);
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to load queue.");
        }
    },
);

export const fetchAdminSupportTicket = createAsyncThunk(
    "collectSupport/fetchAdminOne",
    async (ticketId: string, { rejectWithValue }) => {
        try {
            return await submitGetAdminSupportTicket(ticketId);
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to load ticket.");
        }
    },
);

export const updateAdminSupportTicket = createAsyncThunk(
    "collectSupport/adminUpdate",
    async (
        args: { ticketId: string; payload: IAdminUpdateSupportTicketPayload },
        { rejectWithValue },
    ) => {
        try {
            return await submitUpdateAdminSupportTicket(
                args.ticketId,
                args.payload,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to update ticket.");
        }
    },
);

export const addAdminSupportMessage = createAsyncThunk(
    "collectSupport/adminAddMessage",
    async (
        args: { ticketId: string; body: string; isInternalNote: boolean },
        { rejectWithValue },
    ) => {
        try {
            return await submitAddAdminSupportMessage(
                args.ticketId,
                args.body,
                args.isInternalNote,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to add message.");
        }
    },
);

export const resolveAdminSupportTicket = createAsyncThunk(
    "collectSupport/adminResolve",
    async (
        args: { ticketId: string; note?: string },
        { rejectWithValue },
    ) => {
        try {
            return await submitResolveAdminSupportTicket(
                args.ticketId,
                args.note,
            );
        } catch (e: any) {
            return rejectWithValue(e?.message || "Failed to resolve ticket.");
        }
    },
);

function toPagination(p: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}): Pagination {
    return {
        page: p.page,
        limit: p.limit,
        total: p.total,
        totalPages: p.totalPages,
        hasNext: p.hasNext,
        hasPrev: p.hasPrev,
    };
}

const collectSupportSlice = createSlice({
    name: "collectSupport",
    initialState,
    reducers: {
        clearSupportError(state) {
            state.error = null;
            state.submitError = null;
        },
        clearCurrentTicket(state) {
            state.currentTicket = null;
            state.adminCurrentTicket = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // ── End-user list ──
            .addCase(fetchMySupportTickets.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMySupportTickets.fulfilled, (state, action) => {
                state.isLoading = false;
                state.myTickets = action.payload.items;
                state.myPagination = toPagination(action.payload);
            })
            .addCase(fetchMySupportTickets.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as string) || "Failed to load.";
            })

            // ── End-user detail ──
            .addCase(fetchMySupportTicket.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchMySupportTicket.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentTicket = action.payload;
            })
            .addCase(fetchMySupportTicket.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as string) || "Failed to load.";
            })

            // ── Create ──
            .addCase(createSupportTicket.pending, (state) => {
                state.isSubmitting = true;
                state.submitError = null;
            })
            .addCase(
                createSupportTicket.fulfilled,
                (state, action: PayloadAction<ISupportTicket>) => {
                    state.isSubmitting = false;
                    state.myTickets = [action.payload, ...state.myTickets];
                },
            )
            .addCase(createSupportTicket.rejected, (state, action) => {
                state.isSubmitting = false;
                state.submitError =
                    (action.payload as string) || "Failed to submit.";
            })

            // ── User reply ──
            .addCase(addSupportTicketMessage.pending, (state) => {
                state.isSubmitting = true;
                state.submitError = null;
            })
            .addCase(addSupportTicketMessage.fulfilled, (state, action) => {
                state.isSubmitting = false;
                state.currentTicket = action.payload;
            })
            .addCase(addSupportTicketMessage.rejected, (state, action) => {
                state.isSubmitting = false;
                state.submitError =
                    (action.payload as string) || "Failed to reply.";
            })

            // ── Reopen ──
            .addCase(reopenSupportTicket.fulfilled, (state, action) => {
                if (
                    state.currentTicket &&
                    state.currentTicket.ticket._id === action.payload._id
                ) {
                    state.currentTicket.ticket = action.payload;
                }
            })

            // ── Admin list ──
            .addCase(fetchAdminSupportTickets.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAdminSupportTickets.fulfilled, (state, action) => {
                state.isLoading = false;
                state.adminTickets = action.payload.items;
                state.adminPagination = toPagination(action.payload);
            })
            .addCase(fetchAdminSupportTickets.rejected, (state, action) => {
                state.isLoading = false;
                state.error =
                    (action.payload as string) || "Failed to load queue.";
            })

            // ── Admin detail ──
            .addCase(fetchAdminSupportTicket.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchAdminSupportTicket.fulfilled, (state, action) => {
                state.isLoading = false;
                state.adminCurrentTicket = action.payload;
            })
            .addCase(fetchAdminSupportTicket.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as string) || "Failed to load.";
            })

            // ── Admin mutations reflect into the open ticket ──
            .addCase(updateAdminSupportTicket.pending, (state) => {
                state.isSubmitting = true;
                state.submitError = null;
            })
            .addCase(updateAdminSupportTicket.fulfilled, (state, action) => {
                state.isSubmitting = false;
                if (
                    state.adminCurrentTicket &&
                    state.adminCurrentTicket.ticket._id === action.payload._id
                ) {
                    state.adminCurrentTicket.ticket = action.payload;
                }
            })
            .addCase(updateAdminSupportTicket.rejected, (state, action) => {
                state.isSubmitting = false;
                state.submitError =
                    (action.payload as string) || "Failed to update.";
            })

            .addCase(addAdminSupportMessage.pending, (state) => {
                state.isSubmitting = true;
                state.submitError = null;
            })
            .addCase(
                addAdminSupportMessage.fulfilled,
                (state, action: PayloadAction<ISupportTicketMessage>) => {
                    state.isSubmitting = false;
                    if (state.adminCurrentTicket) {
                        state.adminCurrentTicket.messages = [
                            ...state.adminCurrentTicket.messages,
                            action.payload,
                        ];
                    }
                },
            )
            .addCase(addAdminSupportMessage.rejected, (state, action) => {
                state.isSubmitting = false;
                state.submitError =
                    (action.payload as string) || "Failed to add message.";
            })

            .addCase(resolveAdminSupportTicket.pending, (state) => {
                state.isSubmitting = true;
                state.submitError = null;
            })
            .addCase(resolveAdminSupportTicket.fulfilled, (state, action) => {
                state.isSubmitting = false;
                if (
                    state.adminCurrentTicket &&
                    state.adminCurrentTicket.ticket._id === action.payload._id
                ) {
                    state.adminCurrentTicket.ticket = action.payload;
                }
            })
            .addCase(resolveAdminSupportTicket.rejected, (state, action) => {
                state.isSubmitting = false;
                state.submitError =
                    (action.payload as string) || "Failed to resolve.";
            });
    },
});

export const { clearSupportError, clearCurrentTicket } =
    collectSupportSlice.actions;

export default collectSupportSlice.reducer;
